import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import ContractForm from '../components/Forms/ContractForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiFileText, FiCalendar, FiDollarSign } = FiIcons;

const Contracts = () => {
  const { contracts, clients, addContract, updateContract, deleteContract } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);

  const handleAddContract = () => {
    setEditingContract(null);
    setIsModalOpen(true);
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingContract) {
      updateContract(editingContract.id, data);
    } else {
      addContract(data);
    }
    setIsModalOpen(false);
    setEditingContract(null);
  };

  const handleDeleteContract = (id) => {
    if (window.confirm('Are you sure you want to delete this contract? This will also delete all associated change requests.')) {
      deleteContract(id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Contracts</h1>
        <Button onClick={handleAddContract}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Contract
        </Button>
      </div>

      {contracts.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiFileText} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts yet</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first contract.</p>
          <Button onClick={handleAddContract}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Contract
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {contracts.map((contract, index) => {
            const client = clients.find(c => c.id === contract.clientId);
            return (
              <motion.div
                key={contract.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {contract.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {client?.name} - {client?.company}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditContract(contract)}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                        >
                          <SafeIcon icon={FiEdit} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContract(contract.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                      <span>
                        {format(new Date(contract.startDate), 'MMM dd, yyyy')} - {format(new Date(contract.endDate), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    {contract.value && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiDollarSign} className="h-4 w-4 mr-2" />
                        <span>${parseFloat(contract.value).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Project Scope</h4>
                    <p className="text-sm text-gray-600 line-clamp-3">{contract.projectScope}</p>
                  </div>

                  {contract.paymentTerms && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Payment Terms</h4>
                      <p className="text-sm text-gray-600">{contract.paymentTerms}</p>
                    </div>
                  )}

                  {contract.notes && (
                    <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Notes</h4>
                      <p className="text-sm text-gray-600">{contract.notes}</p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Created {format(new Date(contract.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Updated Modal component to fix overlay issue */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingContract ? 'Edit Contract' : 'Add New Contract'}
        size="xl"
      >
        <ContractForm
          contract={editingContract}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Contracts;